import { useState } from "react";
import { useParams } from "react-router-dom";
import { usePathToken } from "../utils/addressSecrets";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Spinner } from "../components/atoms/Spinner";
import { HiOutlineCheckCircle, HiOutlineBan } from "react-icons/hi";

import EndUserPublicLayout from "../components/atoms/EndUserPublicLayout";
import EndUserMessageCard from "../components/common/EndUserMessageCard";
import { CustomButton } from "../components/atoms/CustomButton";
import {
  useUnsubscribeInfo,
  useConfirmUnsubscribe,
} from "../hooks/unsubscribe";

const UnsubscribePage = () => {
  const { t } = useTranslation("clientView");
  const {
    entityId,
    userId,
    token: tokenSegment,
  } = useParams<{
    entityId: string;
    userId: string;
    token: string;
  }>();
  const token = usePathToken(tokenSegment);

  const {
    data: branding,
    isLoading,
    isError,
  } = useUnsubscribeInfo(entityId || "", userId || "", token || "");

  const { mutate: confirmUnsubscribe, isLoading: isSubmitting } =
    useConfirmUnsubscribe();
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  const senderName =
    branding?.entity_name || (t("unsubscribe.thisSender") as string);

  const handleConfirm = () => {
    setFailed(false);
    confirmUnsubscribe(
      { entityId: entityId || "", userId: userId || "", token: token || "" },
      {
        onSuccess: () => setDone(true),
        onError: () => setFailed(true),
      }
    );
  };

  const head = (
    <Helmet>
      <title>{`Super User Manager - ${t("unsubscribe.pageTitle")}`}</title>
      <meta name="robots" content="noindex" />
    </Helmet>
  );

  if (isLoading) {
    return (
      <>
        {head}
        <div className="flex min-h-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        {head}
        <EndUserMessageCard
          Icon={HiOutlineBan}
          iconColor="red"
          title={t("unsubscribe.invalidTitle")}
          message={t("unsubscribe.invalidMessage")}
        />
      </>
    );
  }

  const commonLayoutProps = {
    entityLogo: branding?.entity_logo,
    entityName: branding?.entity_name,
    showFooter: false,
  };

  if (done) {
    return (
      <>
        {head}
        <EndUserPublicLayout
          {...commonLayoutProps}
          title={t("unsubscribe.doneTitle") as string}
        >
          <div className="flex flex-col items-center text-center gap-3">
            <HiOutlineCheckCircle className="text-green-500" size={48} />
            <p className="text-sm text-gray-500">
              {t("unsubscribe.doneMessage", { sender: senderName })}
            </p>
          </div>
        </EndUserPublicLayout>
      </>
    );
  }

  return (
    <>
      {head}
      <EndUserPublicLayout
        {...commonLayoutProps}
        title={t("unsubscribe.confirmTitle") as string}
        subtitle={
          t("unsubscribe.confirmMessage", { sender: senderName }) as string
        }
      >
        <div className="flex flex-col items-center gap-3">
          <CustomButton
            variant="primary"
            title={t("unsubscribe.confirmButton")}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleConfirm}
          />
          {failed && (
            <p className="text-sm text-red-600">
              {t("unsubscribe.failedMessage")}
            </p>
          )}
        </div>
      </EndUserPublicLayout>
    </>
  );
};

export default UnsubscribePage;
